import { Injectable, Inject } from "@angular/core";
import { IObjectObserver } from "../../services/IObjectObserver";
import { BonusactionDataModel, BonusactionDetailViewModel, BonusactionBenefitViewModel, BonusactionBenefitDataModel, BonuspointsView, BonusPointsViewModel, BonuspointsDataModel, BonusPointsLoadState } from "../../shared/models";
import { LikeService } from "../../services/like.service";
import { UserHelperService } from "../../services/userHelper.service";
import { CircularInjectionHelper } from "../../services/circularInjectionHelper";
import { ObjectObserverFactory } from "../../services/objectObserverFactory";
import { ContentType, ImageOrientation } from "../../shared/contentTypes";
import { BehaviorSubject, Observable } from "rxjs";
import DateManager from "../../shared/dateManager";
import * as moment from "moment";
import { RatingService } from "../../services/rating.service";
import { LocationService } from "../location/location.service";
import { WebVoucherService } from "../../services/web_voucher.service";
import { CoreCOMService } from "../../services/com.service";
import { LocalKeys, StorageHive } from "../../services/valueStorage.service";
import { BenefitRedemptionService } from "../../services/benefitRedemption.service";

@Injectable({
  providedIn: "root"
})
export class BonusSystemDetailService {
  private _bonusObsServer: IObjectObserver<BonusactionDataModel>;
  private _bonusBenefitObsServer: IObjectObserver<BonusactionBenefitDataModel>;

  constructor(private _likeService: LikeService,
    private _webVoucherService: WebVoucherService,
    @Inject(LocationService) private _locationService: LocationService,
    private _ratingService: RatingService,
    private _coreComService: CoreCOMService,
    private _userHelperService: UserHelperService,
    private _objObsInjectHelper: CircularInjectionHelper,
    private _benefitRedemptionService: BenefitRedemptionService) {
    this._bonusObsServer = ObjectObserverFactory.GetObjectObserverByInjectionHelper<BonusactionDataModel>(ContentType.Bonusaction, _objObsInjectHelper);
    this._bonusBenefitObsServer = ObjectObserverFactory.GetObjectObserverByInjectionHelper<BonusactionBenefitDataModel>(ContentType.BonusactionBenefit, _objObsInjectHelper);
  }

  public get ObserveChanges(): Observable<(string | number)[]> {
    return this._bonusObsServer.ObserveChanges;
  }

  public get BenefitObserveChanges(): Observable<(string | number)[]> {
    return this._bonusBenefitObsServer.ObserveChanges;
  }

  /**
   * Getting the BonusactionViewModel base on Id
   */
  public GetBonusActionDetailViewModel(bonusActionId: number | string): Promise<BonusactionDetailViewModel> {
    const me = this;
    let isTestUser: boolean = false;
    return new Promise<BonusactionDetailViewModel>((resolve, reject) => {
      isTestUser = me._userHelperService.IsTestUser;
      me._bonusObsServer.GetObject(bonusActionId)
        .then((bonusActions) => {
          me.mapBonusactionToBonusActionDetailViewModel(bonusActions)
            .then(resolve)
            .catch((e) => {
              me._objObsInjectHelper.coreLogService.error("BonusSystemDetailService.GetBonusActionDetailViewModel", "mapBonusactionToBonusActionDetailViewModel", e);
              reject();
            });
        })
        .catch((e) => {
          me._objObsInjectHelper.coreLogService.error("BonusSystemDetailService.GetBonusActionDetailViewModel", "GetObject", e);
          reject();
        });
    });
  }

  /**
   * Update bonus point details in view model
   */
  public UpdateBonusPointDetail(bonusactionDetailViewModel: BonusactionDetailViewModel, bonuspointsView: BonuspointsView): void {
    if (bonuspointsView.actions && bonuspointsView.actions.filter(f => f.id === bonusactionDetailViewModel.id)[0]) {
      bonusactionDetailViewModel.points = bonuspointsView.actions.filter(f => f.id === bonusactionDetailViewModel.id)[0].points;
    } else {
      bonusactionDetailViewModel.points = 0;
    }
    this._benefitRedemptionService.recalculateBenefitPercentages(bonusactionDetailViewModel);
  }

  /**
   * Check if bonus points have been changed, then load and update
   */
  public async BonusActionDetailInterval(bonusactionDetailViewModel: BonusactionDetailViewModel): Promise<void> {
    const me = this;
    try {
      if (!me._userHelperService.IsRegistered) {
        bonusactionDetailViewModel.pointsLoadState = BonusPointsLoadState.NotAvailable;
        return;
      }
      bonusactionDetailViewModel.pointsLoadState = BonusPointsLoadState.Loading;
      const bonusPointsStorage: BonuspointsDataModel = await me._objObsInjectHelper.yourAppServer.coreCOMService.environment.valueStorage.get(StorageHive.Local, LocalKeys.BonuspointsDataModel)
      const lastPointAction = WebVoucherService.getLastPointActionFromBonusPointString(bonusPointsStorage);
      const bonuspointsDataModel = await me._webVoucherService.GetNewBounspoints(lastPointAction);

      if (bonuspointsDataModel.bonuspoints.length > 0) {
        me.UpdateBonusPointDetail(bonusactionDetailViewModel, WebVoucherService.convertBonusPointStringToBonusPointsView(bonuspointsDataModel));
        me._objObsInjectHelper.yourAppServer.coreCOMService.environment.valueStorage.set(StorageHive.Local, LocalKeys.BonuspointsDataModel, bonuspointsDataModel);

      } else {
        // do nothing
      }
      bonusactionDetailViewModel.pointsLoadState = BonusPointsLoadState.Success;
    } catch (e) {
      bonusactionDetailViewModel.pointsLoadState = BonusPointsLoadState.Error;
      me._objObsInjectHelper.coreLogService.error("BonusSystemDetailService.BonusActionDetailInterval", "try catch", e);
    }
  }

  /**
   * Map a BonusActionDataModel to BonusActionDetailViewModel
   */
  private async mapBonusactionToBonusActionDetailViewModel(model: BonusactionDataModel): Promise<BonusactionDetailViewModel> {
    const me = this;
    if (model) {
      const viewModel = new BonusactionDetailViewModel();
      viewModel.id = model.id;
      viewModel.labelDE = model.label_de;
      viewModel.descriptionDE = model.description_de.replace("\n", "<br>");
      viewModel.descriptionEN = model.description_en.replace("\n", "<br>");
      viewModel.startDateYMD = DateManager.MomentDateTimeFormatter(model.start_at, true, false);
      viewModel.endDateYMD = DateManager.MomentDateTimeFormatter(model.end_at, model.has_end_at, false);
      viewModel.image_photo = ContentType.Bonussystem + "/" + model.id + "_photo_" + ImageOrientation.Landscape + "/" + model.last_image_update_at_ymd + "/" + model.last_image_update_at_hms;
      viewModel.showBenefitText = false;
      viewModel.benefitText = "BenefitSample";
      viewModel.hasEnd = model.has_end_at;
      viewModel.pointsLoadState = BonusPointsLoadState.NotLoaded;
      try {
        viewModel.like = await me._likeService.HasLike(ContentType.Bonussystem, model.id);
      } catch (error) {
        me._objObsInjectHelper.coreLogService.error("BonusSystemDetailService.mapBonusactionToBonusActionDetailViewModel", "HasLike try catch", error);
      }

      try {
        viewModel.rate = await me._ratingService.GetRatingStars(ContentType.Bonussystem, model.id);
      } catch (error) {
        me._objObsInjectHelper.coreLogService.error("BonusSystemDetailService.mapBonusactionToBonusActionDetailViewModel", "GetRatingStars try catch", error);
      }

      try {
        if (model.locations_participation) {
          viewModel.locations = await me._locationService.GetBunosActionLocation(model.locations);
        } else {
          viewModel.locations = await me._locationService.GetBlackListBunosActionLocation(model.locations);
        }
      } catch (error) {
        me._objObsInjectHelper.coreLogService.error("BonusSystemDetailService.mapBonusactionToBonusActionDetailViewModel", "GetLocation try catch", error);
      }
      if (!me._userHelperService.IsRegistered) {
        viewModel.pointsLoadState = BonusPointsLoadState.NotAvailable;
      } else {
        try {
          const bonusPoinsView = await me.GetBounspointsViewModel();
          if (bonusPoinsView) {
            viewModel.storageDate = DateManager.MomentDateTimeFormatter(bonusPoinsView.storageDate, true, true);
            if (bonusPoinsView.hasData) {
              viewModel.pointsLoadState = BonusPointsLoadState.Success;
              if (bonusPoinsView.actions.filter(f => f.id === model.id)[0]) {
                viewModel.points = bonusPoinsView.actions.filter(f => f.id === model.id)[0].points;
              } else {
                // Valid data received, but no specific action found - this is Success with 0 points
                viewModel.points = 0;
              }
            } else {
              viewModel.points = 0;
              viewModel.pointsLoadState = BonusPointsLoadState.Error;
              me._objObsInjectHelper.coreLogService.error("BonusSystemDetailService.mapBonusactionToBonusActionDetailViewModel", `GetBounspoints errorCode: ${bonusPoinsView.errorCode} errorMessage`, bonusPoinsView.errorMessage);
            }
          } else {
            viewModel.pointsLoadState = BonusPointsLoadState.Error;
            viewModel.points = 0;
          }

          const benefitDataModels = await me._bonusBenefitObsServer.GetObjectsByFilter(b => b.bonusaction_id === model.id && !b.deleted);
          viewModel.benefits = [];
          viewModel.benefits.push(...me.mapToBonusBenefitViewModelList(benefitDataModels, viewModel.points || 0));
        } catch (error) {
          viewModel.pointsLoadState = BonusPointsLoadState.Error;
          viewModel.points = 0;
          me._objObsInjectHelper.coreLogService.error("BonusSystemDetailService.mapBonusactionToBonusActionDetailViewModel", "GetBounspoints try catch", error);
        }
      }

      return Promise.resolve(viewModel);
    } else {
      return Promise.resolve(null);
    }
  }

  /**
   * Get bonus points view model with proper error handling
   */
  public GetBounspointsViewModel(): Promise<BonusPointsViewModel> {
    const me = this;
    return new Promise<BonusPointsViewModel>(async (resolve, reject) => {
      try {
        if (!me._userHelperService.IsRegistered) {
          resolve(null);
          return;
        }
        let viewModel: BonusPointsViewModel = null;
        const bonusPointsStorage: BonuspointsDataModel = await me._objObsInjectHelper.yourAppServer.coreCOMService.environment.valueStorage.get(StorageHive.Local, LocalKeys.BonuspointsDataModel);

        if (bonusPointsStorage) {
          viewModel = me.mapToBonusPointViewModel(WebVoucherService.convertBonusPointStringToBonusPointsView(bonusPointsStorage));
          resolve(viewModel);
        } else if (me._coreComService.environment.networkInformation.isOnline) {
          const bonuspointsDataModel = await me._webVoucherService.GetBounspoints();
          me._objObsInjectHelper.yourAppServer.coreCOMService.environment.valueStorage.set(StorageHive.Local, LocalKeys.BonuspointsDataModel, bonuspointsDataModel);
          viewModel = me.mapToBonusPointViewModel(WebVoucherService.convertBonusPointStringToBonusPointsView(bonuspointsDataModel));
          resolve(viewModel);
        } else {
          resolve(viewModel);
        }
      } catch (error) {
        me._objObsInjectHelper.coreLogService.error("BonusSystemDetailService.GetBounspointsViewModel", "try catch", error);
        reject(error);
      }
    });
  }

  private mapToBonusPointViewModel(model: BonuspointsView): BonusPointsViewModel {
    if (model) {
      const viewModel = new BonusPointsViewModel();
      viewModel.actions = model.actions;
      viewModel.errorCode = model.errorCode;
      viewModel.errorMessage = model.errorMessage;
      viewModel.hasData = model.hasData;
      viewModel.lastPointAction = model.lastPointAction;
      viewModel.using = model.using;
      viewModel.storageDate = moment();
      return viewModel;
    } else {
      return null;
    }
  }

  private mapToBonusBenefitViewModel(model: BonusactionBenefitDataModel, points: number): BonusactionBenefitViewModel {
    const viewModel = new BonusactionBenefitViewModel();
    viewModel.product_id = model.product_id;
    viewModel.bonusaction_id = model.bonusaction_id;
    viewModel.loss_id = model.loss_id;
    viewModel.id = model.id;
    viewModel.label_de = model.label_de;
    viewModel.cost = model.points;
    viewModel.benefitpercantage = this._benefitRedemptionService.calculateBenefitPercentage(points, model.points);
    viewModel.image_photo = ContentType.Bonussystem_BenefitImage + "/" + model.id + "_photo_" + ImageOrientation.Landscape + "/" + model.last_image_update_at_ymd + "/" + model.last_image_update_at_hms;
    return viewModel;
  }

  private mapToBonusBenefitViewModelList(modelList: BonusactionBenefitDataModel[], points: number): BonusactionBenefitViewModel[] {
    const me = this;
    if (modelList && modelList.length > 0) {
      const result: BonusactionBenefitViewModel[] = [];
      modelList.forEach(model => {
        result.push(me.mapToBonusBenefitViewModel(model, points));
      });
      return result;
    } else {
      return null;
    }
  }
}